// The streets basemap, drawn from the OpenFreeMap vector source the map already loads.
//
// It replaces a raster basemap from CARTO. That was not a styling decision: CARTO's free tier is
// non-commercial only, which a public site breaches on the day it launches regardless of whether it
// ever takes money. OpenFreeMap's public instance permits commercial use with no key, no
// registration and no stated request ceiling, and the app already depends on it for labels,
// buildings and glyphs — so this removes a third party rather than swapping one for another.
//
// Two things follow from being vector rather than raster, and both are why this is worth the layer
// list. Labels are already drawn from this same source on top of the overlays, so the basemap and
// its labels can no longer disagree about zoom or projection. And the palette is ours: this map's
// subject is red no-fly and green allowed polygons, so the ground under them is deliberately low in
// chroma. Anything saturated down here competes with the only colours that carry meaning.
//
// Colour and width choices approximate the raster basemap they replace, so the zone overlays sit on
// roughly the tone they were tuned against.

import type { ExpressionSpecification, LayerSpecification } from 'maplibre-gl';

/** The vector source id these layers read. Declared by the engine's style. */
export const STREETS_SOURCE = 'openfreemap';

/**
 * Attribution required by OpenFreeMap's terms.
 *
 * MapLibre renders whatever a source declares, so the string is the whole of the compliance. Their
 * terms name OpenMapTiles alongside OpenStreetMap and make only the OpenFreeMap credit itself
 * optional — the reverse of what is easy to assume from the domain name.
 */
export const STREETS_ATTRIBUTION = '© OpenFreeMap © OpenMapTiles © OpenStreetMap';

const LAND = '#f6f2ed';
const WATER = '#bcd9e8';
const WOOD = '#dfe7d3';
const GRASS = '#e6eddc';
const FARMLAND = '#f2eee1';
const SAND = '#f5eeda';
const BUILT = '#efeae2';
const BUILDING = '#e5ddd2';
const RUNWAY = '#e8e4df';
const RAIL = '#d9d3cb';
const BORDER = '#a8a29a';

// Road tiers. Named by how far out each is worth drawing: a motorway orients you at country zoom, a
// service road is noise until you are looking at a single field.
const MAJOR = ['motorway', 'trunk'];
const MID = ['primary', 'secondary', 'tertiary'];
const MINOR = ['minor', 'service', 'track', 'raceway', 'busway', 'bus_guideway'];

const inClass = (classes: string[]): ExpressionSpecification =>
  ['in', ['get', 'class'], ['literal', classes]] as ExpressionSpecification;

/** Per-zoom line width that also varies by road class, since MapLibre filters cannot read zoom. */
const roadWidth = (near: number, far: number): ExpressionSpecification =>
  ['interpolate', ['exponential', 1.5], ['zoom'], 5, far, 10, far * 2, 14, near, 18, near * 3.2] as ExpressionSpecification;

/**
 * The full streets stack, bottom to top. Ids are all `bm-` prefixed so the engine can flip the whole
 * basemap by prefix rather than by keeping a second list in sync with this one.
 */
export function streetsLayers(visible: boolean): LayerSpecification[] {
  const vis = { visibility: visible ? ('visible' as const) : ('none' as const) };
  const src = { source: STREETS_SOURCE };
  return [
    // A second background under the dark one. Satellite wants dark at the edges of the world; the
    // streets map wants paper.
    { id: 'bm-land', type: 'background', layout: vis, paint: { 'background-color': LAND } },

    { id: 'bm-landcover', ...src, 'source-layer': 'landcover', type: 'fill', layout: vis,
      paint: { 'fill-color': ['match', ['get', 'class'], 'wood', WOOD, 'grass', GRASS, 'farmland', FARMLAND, 'sand', SAND, 'ice', '#ffffff', GRASS] as ExpressionSpecification, 'fill-opacity': 0.9 } },
    { id: 'bm-park', ...src, 'source-layer': 'park', type: 'fill', layout: vis,
      paint: { 'fill-color': WOOD, 'fill-opacity': 0.5 } },
    { id: 'bm-landuse', ...src, 'source-layer': 'landuse', type: 'fill', layout: vis,
      filter: inClass(['residential', 'commercial', 'industrial', 'retail', 'suburb', 'quarter', 'neighbourhood']),
      paint: { 'fill-color': BUILT } },

    { id: 'bm-water', ...src, 'source-layer': 'water', type: 'fill', layout: vis,
      // `brunnel: tunnel` is water under something else — drawing it paints rivers across the ground.
      filter: ['!=', ['get', 'brunnel'], 'tunnel'] as ExpressionSpecification,
      paint: { 'fill-color': WATER } },
    { id: 'bm-waterway', ...src, 'source-layer': 'waterway', type: 'line', minzoom: 8, layout: { ...vis, 'line-cap': 'round' },
      paint: { 'line-color': WATER, 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 14, 2.4] as ExpressionSpecification } },

    { id: 'bm-aeroway-fill', ...src, 'source-layer': 'aeroway', type: 'fill', minzoom: 10, layout: vis,
      paint: { 'fill-color': RUNWAY } },
    { id: 'bm-aeroway-line', ...src, 'source-layer': 'aeroway', type: 'line', minzoom: 10, layout: vis,
      paint: { 'line-color': RUNWAY, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1, 15, 6] as ExpressionSpecification } },

    // Casing first, then fill, so junctions read as continuous roads rather than stacked strokes.
    { id: 'bm-road-case', ...src, 'source-layer': 'transportation', type: 'line', minzoom: 4,
      filter: inClass([...MAJOR, ...MID]), layout: { ...vis, 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#e3d9c8', 'line-width': roadWidth(5.4, 1.5) } },
    { id: 'bm-road-major', ...src, 'source-layer': 'transportation', type: 'line', minzoom: 4,
      filter: inClass(MAJOR), layout: { ...vis, 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#f6c489', 'line-width': roadWidth(4, 1.1) } },
    { id: 'bm-road-mid', ...src, 'source-layer': 'transportation', type: 'line', minzoom: 7,
      filter: inClass(MID), layout: { ...vis, 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#fdeacd', 'line-width': roadWidth(3.2, 0.7) } },
    { id: 'bm-road-minor', ...src, 'source-layer': 'transportation', type: 'line', minzoom: 12,
      filter: inClass(MINOR), layout: { ...vis, 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.6, 18, 6] as ExpressionSpecification } },
    { id: 'bm-rail', ...src, 'source-layer': 'transportation', type: 'line', minzoom: 9,
      filter: ['==', ['get', 'class'], 'rail'] as ExpressionSpecification, layout: vis,
      paint: { 'line-color': RAIL, 'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 16, 2.4] as ExpressionSpecification } },

    // 2D building footprints. The 3D extrusions are a separate layer the engine owns, shown only in
    // 3D — these are the flat plans that make a town read as a town when looking straight down.
    { id: 'bm-building', ...src, 'source-layer': 'building', type: 'fill', minzoom: 14, layout: vis,
      paint: { 'fill-color': BUILDING, 'fill-opacity': ['interpolate', ['linear'], ['zoom'], 14, 0, 15.5, 1] as ExpressionSpecification } },

    // Country borders matter on this map: a restriction stops at one, and Türkiye has eight neighbours.
    { id: 'bm-boundary', ...src, 'source-layer': 'boundary', type: 'line', layout: { ...vis, 'line-join': 'round' },
      filter: ['all', ['<=', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]] as ExpressionSpecification,
      paint: {
        'line-color': BORDER,
        'line-dasharray': [3, 1.5],
        'line-width': ['interpolate', ['linear'], ['zoom'], 3, ['match', ['get', 'admin_level'], 2, 1.1, 0.5], 10, ['match', ['get', 'admin_level'], 2, 2.4, 1]] as ExpressionSpecification,
        'line-opacity': ['match', ['get', 'admin_level'], 2, 0.9, 0.45] as ExpressionSpecification,
      } },
  ];
}

/** Every id `streetsLayers` produces — the set the basemap toggle flips. */
export const STREETS_LAYER_IDS: string[] = streetsLayers(true).map((l) => l.id);
